import apiCall from './api';

/**
 * Service pour la gestion des promotions
 */
export const promotionService = {

  // ==================== CRUD PROMOTIONS ====================

  /**
   * Créer une nouvelle promotion
   */
  createPromotion: async (promotionData) => {
    try {
      console.log('📦 Création promotion:', promotionData);

      const payload = {
        name: promotionData.name,
        description: promotionData.description || null,
        promotion_type: promotionData.promotionType,
        start_date: promotionData.startDate || null,
        end_date: promotionData.endDate || null,
        remaining_sales: promotionData.remainingSales || 1,
        apply_to_all: promotionData.applyToAll || false,
        discount_percentage: promotionData.discountPercentage || null,
        discount_amount: promotionData.discountAmount || null,
        products: (promotionData.products || []).map(p => ({
          product_id: p.productId,
          promo_price: p.promoPrice,
          discount_percentage: p.discountPercentage || null
        })),
        activate_now: promotionData.activateNow || false
      };

      const response = await apiCall.post('/manager/promotions', payload);

      if (response.success) {
        console.log('✅ Promotion créée:', response.data);
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion créée avec succès'
        };
      }

      return { success: false, message: response.message || 'Erreur lors de la création' };

    } catch (error) {
      console.error('❌ Erreur création promotion:', error);
      return { success: false, message: error.message || 'Erreur de connexion' };
    }
  },

  /**
   * Récupérer toutes les promotions
   */
  getAllPromotions: async () => {
    try {
      console.log('📋 Récupération des promotions...');

      const response = await apiCall.get('/manager/promotions');

      if (response.success && Array.isArray(response.data)) {
        const promotions = response.data.map(p => promotionService.formatPromotionForDisplay(p));
        console.log(`✅ ${promotions.length} promotion(s) récupérée(s)`);
        return { success: true, data: promotions };
      }

      return { success: false, data: [], message: 'Aucune promotion trouvée' };

    } catch (error) {
      console.error('❌ Erreur récupération promotions:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Récupérer les promotions actives
   */
  getActivePromotions: async () => {
    try {
      console.log('🚀 Récupération des promotions actives...');

      const response = await apiCall.get('/manager/promotions/active');

      if (response.success && Array.isArray(response.data)) {
        const promotions = response.data.map(p => promotionService.formatPromotionForDisplay(p));
        console.log(`✅ ${promotions.length} promotion(s) active(s)`);
        return { success: true, data: promotions };
      }

      return { success: false, data: [], message: 'Aucune promotion active' };

    } catch (error) {
      console.error('❌ Erreur récupération promotions actives:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * Récupérer une promotion par ID
   */
  getPromotionById: async (promotionId) => {
    try {
      console.log('🔍 Récupération promotion:', promotionId);

      const response = await apiCall.get(`/manager/promotions/${promotionId}`);

      if (response.success) {
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data)
        };
      }

      return { success: false, data: null, message: response.message };

    } catch (error) {
      console.error('❌ Erreur récupération promotion:', error);
      return { success: false, data: null, message: error.message };
    }
  },

  /**
   * Mettre à jour une promotion
   */
  updatePromotion: async (promotionId, updateData) => {
    try {
      console.log('✏️ Mise à jour promotion:', promotionId);

      const payload = {};
      
      if (updateData.name !== undefined) payload.name = updateData.name;
      if (updateData.description !== undefined) payload.description = updateData.description;
      if (updateData.promotionType !== undefined) payload.promotion_type = updateData.promotionType;
      if (updateData.status !== undefined) payload.status = updateData.status;
      if (updateData.startDate !== undefined) payload.start_date = updateData.startDate;
      if (updateData.endDate !== undefined) payload.end_date = updateData.endDate;
      if (updateData.remainingSales !== undefined) payload.remaining_sales = updateData.remainingSales;
      if (updateData.applyToAll !== undefined) payload.apply_to_all = updateData.applyToAll;
      if (updateData.discountPercentage !== undefined) payload.discount_percentage = updateData.discountPercentage;
      if (updateData.discountAmount !== undefined) payload.discount_amount = updateData.discountAmount;
      
      if (updateData.productsToAdd) {
        payload.products_to_add = updateData.productsToAdd.map(p => ({
          product_id: p.productId,
          promo_price: p.promoPrice,
          discount_percentage: p.discountPercentage || null
        }));
      }
      
      if (updateData.productIdsToRemove) {
        payload.product_ids_to_remove = updateData.productIdsToRemove;
      }

      const response = await apiCall.put(`/manager/promotions/${promotionId}`, payload);

      if (response.success) {
        console.log('✅ Promotion mise à jour');
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion mise à jour'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur mise à jour promotion:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Supprimer une promotion
   */
  deletePromotion: async (promotionId) => {
    try {
      console.log('🗑️ Suppression promotion:', promotionId);

      const response = await apiCall.delete(`/manager/promotions/${promotionId}`);

      if (response.success) {
        console.log('✅ Promotion supprimée');
        return { success: true, message: 'Promotion supprimée' };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur suppression promotion:', error);
      return { success: false, message: error.message };
    }
  },

  // ==================== ACTIVATION / DÉSACTIVATION ====================

  /**
   * Activer une promotion
   */
  activatePromotion: async (promotionId) => {
    try {
      console.log('🚀 Activation promotion:', promotionId);

      const response = await apiCall.post(`/manager/promotions/${promotionId}/activate`);

      if (response.success) {
        console.log('✅ Promotion activée');
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion activée'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur activation promotion:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Désactiver une promotion
   */
  deactivatePromotion: async (promotionId) => {
    try {
      console.log('⏸️ Désactivation promotion:', promotionId);

      const response = await apiCall.post(`/manager/promotions/${promotionId}/deactivate`);

      if (response.success) {
        console.log('✅ Promotion désactivée');
        return {
          success: true,
          data: promotionService.formatPromotionForDisplay(response.data),
          message: 'Promotion désactivée'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur désactivation promotion:', error);
      return { success: false, message: error.message };
    }
  },

  // ==================== GESTION DES PRODUITS ====================

  /**
   * Ajouter un produit à une promotion
   */
  addProductToPromotion: async (promotionId, productData) => {
    try {
      console.log('➕ Ajout produit à promotion:', promotionId, productData);

      const payload = {
        product_id: productData.productId,
        promo_price: productData.promoPrice,
        discount_percentage: productData.discountPercentage || null
      };

      const response = await apiCall.post(`/manager/promotions/${promotionId}/products`, payload);

      if (response.success) {
        console.log('✅ Produit ajouté');
        return {
          success: true,
          data: promotionService.formatPromotionProductForDisplay(response.data),
          message: 'Produit ajouté à la promotion'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur ajout produit:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Retirer un produit d'une promotion
   */
  removeProductFromPromotion: async (promotionId, productId) => {
    try {
      console.log('➖ Retrait produit de promotion:', promotionId, productId);

      const response = await apiCall.delete(`/manager/promotions/${promotionId}/products/${productId}`);

      if (response.success) {
        console.log('✅ Produit retiré');
        return { success: true, message: 'Produit retiré de la promotion' };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur retrait produit:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Mettre à jour le prix promo d'un produit
   */
  updateProductPromoPrice: async (promotionId, productId, newPromoPrice) => {
    try {
      console.log('✏️ Mise à jour prix promo:', promotionId, productId, newPromoPrice);

      const response = await apiCall.patch(
        `/manager/promotions/${promotionId}/products/${productId}`,
        { promo_price: newPromoPrice }
      );

      if (response.success) {
        console.log('✅ Prix promo mis à jour');
        return {
          success: true,
          data: promotionService.formatPromotionProductForDisplay(response.data),
          message: 'Prix promo mis à jour'
        };
      }

      return { success: false, message: response.message };

    } catch (error) {
      console.error('❌ Erreur mise à jour prix promo:', error);
      return { success: false, message: error.message };
    }
  },

  // ==================== PRIX PROMO ACTIFS (POUR POS) ====================

  /**
   * Récupérer tous les prix promo actifs
   * Utilisé par le POS pour afficher les prix promo
   */
  getActivePromoPrices: async () => {
    try {
      console.log('💰 Récupération des prix promo actifs...');

      const response = await apiCall.get('/manager/promotions/active-prices');

      if (response.success) {
        console.log('✅ Prix promo actifs récupérés:', Object.keys(response.data).length, 'produits');
        return { success: true, data: response.data };
      }

      return { success: false, data: {}, message: response.message };

    } catch (error) {
      console.error('❌ Erreur récupération prix promo:', error);
      return { success: false, data: {}, message: error.message };
    }
  },

  // ==================== FORMATAGE ====================

  /**
   * Formater une promotion pour l'affichage
   */
  formatPromotionForDisplay: (promotion) => {
    if (!promotion) return null;

    return {
      id: promotion.id,
      name: promotion.name,
      description: promotion.description,
      
      promotionType: promotion.promotion_type || promotion.promotionType,
      promotionTypeLabel: promotion.promotion_type_label || promotion.promotionTypeLabel || 
        promotionService.getPromotionTypeLabel(promotion.promotion_type || promotion.promotionType),
      
      status: promotion.status,
      statusLabel: promotion.status_label || promotion.statusLabel ||
        promotionService.getStatusLabel(promotion.status),
      statusColor: promotionService.getStatusColor(promotion.status),
      
      startDate: promotion.start_date || promotion.startDate,
      endDate: promotion.end_date || promotion.endDate,
      remainingSales: promotion.remaining_sales || promotion.remainingSales,
      
      applyToAll: promotion.apply_to_all || promotion.applyToAll || false,
      discountPercentage: promotion.discount_percentage || promotion.discountPercentage,
      discountAmount: promotion.discount_amount || promotion.discountAmount,
      
      boutiqueId: promotion.boutique_id || promotion.boutiqueId,
      createdById: promotion.created_by_id || promotion.createdById,
      createdByName: promotion.created_by_name || promotion.createdByName,
      
      products: (promotion.products || []).map(p => promotionService.formatPromotionProductForDisplay(p)),
      productCount: promotion.product_count || promotion.productCount || (promotion.products || []).length,
      totalSavings: promotion.total_savings || promotion.totalSavings || 0,
      
      isCurrentlyActive: promotion.is_currently_active || promotion.isCurrentlyActive || false,
      isExpired: promotion.is_expired || promotion.isExpired || false,
      
      usedInSaleId: promotion.used_in_sale_id || promotion.usedInSaleId,
      usedAt: promotion.used_at || promotion.usedAt,
      
      createdAt: promotion.created_at || promotion.createdAt,
      updatedAt: promotion.updated_at || promotion.updatedAt
    };
  },

  /**
   * Formater un produit de promotion pour l'affichage
   */
  formatPromotionProductForDisplay: (pp) => {
    if (!pp) return null;

    return {
      id: pp.id,
      promotionId: pp.promotion_id || pp.promotionId,
      productId: pp.product_id || pp.productId,
      productName: pp.product_name || pp.productName,
      productSku: pp.product_sku || pp.productSku,
      productBarcode: pp.product_barcode || pp.productBarcode,
      productImageUrl: pp.product_image_url || pp.productImageUrl,
      categoryName: pp.category_name || pp.categoryName,
      originalPrice: parseFloat(pp.original_price || pp.originalPrice || 0),
      promoPrice: parseFloat(pp.promo_price || pp.promoPrice || 0),
      discountPercentage: parseFloat(pp.discount_percentage || pp.discountPercentage || 0),
      savings: parseFloat(pp.savings || 0),
      currentStock: pp.current_stock || pp.currentStock,
      isActive: pp.is_active || pp.isActive
    };
  },

  // ==================== UTILITAIRES ====================

  /**
   * Obtenir le label du type de promotion
   */
  getPromotionTypeLabel: (type) => {
    const labels = {
      'PERIOD': 'Période',
      'NEXT_SALE': 'Prochaine vente'
    };
    return labels[type] || type;
  },

  /**
   * Obtenir le label du statut
   */
  getStatusLabel: (status) => {
    const labels = {
      'DRAFT': 'Brouillon',
      'ACTIVE': 'Active',
      'INACTIVE': 'Inactive',
      'EXPIRED': 'Expirée',
      'COMPLETED': 'Terminée'
    };
    return labels[status] || status;
  },

  /**
   * Obtenir la couleur du statut
   */
  getStatusColor: (status) => {
    const colors = {
      'DRAFT': 'secondary',
      'ACTIVE': 'success',
      'INACTIVE': 'warning',
      'EXPIRED': 'danger',
      'COMPLETED': 'info'
    };
    return colors[status] || 'secondary';
  },

  /**
   * Obtenir l'icône du type de promotion
   */
  getPromotionTypeIcon: (type) => {
    const icons = {
      'PERIOD': '📅',
      'NEXT_SALE': '🎯'
    };
    return icons[type] || '🏷️';
  },

  /**
   * Formater le prix
   */
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  },

  /**
   * Formater la date
   */
  formatDate: (dateString) => {
    if (!dateString) return 'Non définie';
    
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  /**
   * Calculer le prix promo à partir d'un pourcentage
   */
  calculatePromoPrice: (originalPrice, discountPercentage) => {
    if (!originalPrice || !discountPercentage) return originalPrice;
    const discount = (originalPrice * discountPercentage) / 100;
    return Math.round(originalPrice - discount);
  },

  /**
   * Calculer le pourcentage de réduction
   */
  calculateDiscountPercentage: (originalPrice, promoPrice) => {
    if (!originalPrice || originalPrice <= 0) return 0;
    const discount = originalPrice - promoPrice;
    return Math.round((discount / originalPrice) * 100);
  },

  /**
   * Valider les données d'une promotion
   */
  validatePromotion: (data) => {
    const errors = [];

    if (!data.name || data.name.trim().length < 2) {
      errors.push('Le nom doit contenir au moins 2 caractères');
    }

    if (!data.promotionType) {
      errors.push('Le type de promotion est requis');
    }

    if (data.promotionType === 'PERIOD') {
      if (data.endDate && new Date(data.endDate) < new Date()) {
        errors.push('La date de fin ne peut pas être dans le passé');
      }
      if (data.startDate && data.endDate && new Date(data.endDate) < new Date(data.startDate)) {
        errors.push('La date de fin doit être après la date de début');
      }
    }

    if (!data.applyToAll && (!data.products || data.products.length === 0)) {
      errors.push('Ajoutez au moins un produit ou activez "Appliquer à tous"');
    }

    if (data.applyToAll && !data.discountPercentage && !data.discountAmount) {
      errors.push('Définissez un pourcentage ou un montant de réduction');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default promotionService;